import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  adicionarMeses,
  diasNoMes,
  ROTULO_CENTRO_DE_CUSTO,
  ROTULO_FORMA_PAGAMENTO,
  type PaginaDoExtrato,
  type ResumoFinanceiro,
} from '@arena/shared/schemas';
import { dataCurta, dinheiro } from '../agenda/formatos.js';
import { CardDeHorasACobrar } from '../agenda/HorasACobrar.jsx';
import { ErroDeApi, ErroDeRede } from '../api/cliente.js';
import { financeiroApi } from '../api/financeiro.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Barras } from '../graficos/Barras.jsx';
import { BarrasHorizontais } from '../graficos/BarrasHorizontais.jsx';
import { Erro, EsqueletoDeCard, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Botao, Card, Kpi, Status, Tabela, Tag, Vazio, type Tendencia } from '../ui/primitivos.jsx';
import { ModalDeLancamento } from './ModalDeLancamento.jsx';

/**
 * `#v-fin` — o razão: caixa, entradas e saídas por semana, saídas por
 * categoria, extrato de últimos lançamentos.
 *
 * A "Projeção de caixa · 90 dias" do protótipo fica de fora — os
 * documentos 16 e 17 entregam, porque dependem de dados que este
 * módulo ainda não tem (ver Arena_backend/Docs/plano/11-financeiro-
 * razao.md, seção Escopo).
 *
 * TODOS os números vêm de `resumo`, já calculados no servidor SOBRE AS
 * LINHAS QUE O PERFIL ALCANÇA — para a Gestão, sem a folha em nenhum
 * total. A tela só formata; nunca soma de novo.
 *
 * Ver: Arena_backend/Docs/plano/11-financeiro-razao.md
 */

// As mesmas semanas nunca têm mês para selecionar nesta fase — a chave
// ainda carrega um terceiro segmento para já ficar pronta quando a
// tela ganhar navegação de mês (documentos 16/17).
const MES_ATUAL = undefined;

const CORES_DAS_CATEGORIAS = [
  'var(--serie-1)',
  'var(--serie-2)',
  'var(--serie-3)',
  'var(--serie-4)',
  'var(--serie-5)',
];

export function PaginaDoFinanceiro() {
  const [params, setParams] = useSearchParams();
  const { pode } = useSessao();

  const resumoQuery = useQuery({
    queryKey: ['financeiro', 'resumo', MES_ATUAL ?? 'atual'],
    queryFn: () => financeiroApi.resumo(MES_ATUAL),
  });
  const extratoQuery = useQuery({
    queryKey: ['financeiro', 'extrato', MES_ATUAL ?? 'atual'],
    queryFn: () => financeiroApi.extrato({ mes: MES_ATUAL }),
  });
  // A invalidação de ['financeiro'] ao criar acontece DENTRO do modal
  // (mesmo padrão de `ModalDeReserva.tsx`): quem sabe que teve sucesso
  // é quem chama a mutação.

  const podeCriar = pode('financeiro', 'criar');
  const novo = params.get('novo') === '1';

  function abrirNovo() {
    const novos = new URLSearchParams(params);
    novos.set('novo', '1');
    setParams(novos, { replace: true });
  }
  function fecharNovo() {
    const novos = new URLSearchParams(params);
    novos.delete('novo');
    setParams(novos, { replace: true });
  }

  if (resumoQuery.isPending) {
    return (
      <>
        <CabecalhoDoFinanceiro titulo="Carregando o financeiro…" />
        <div className="grid g4">
          {[0, 1, 2, 3].map((i) => (
            <EsqueletoDeCard key={i} linhas={2} />
          ))}
        </div>
        <EsqueletoDeCard linhas={8} />
      </>
    );
  }

  if (resumoQuery.isError) {
    const erro = resumoQuery.error;
    return (
      <>
        <CabecalhoDoFinanceiro titulo="Não consegui carregar o financeiro" />
        <Erro
          tipo={tipoDoErro(erro)}
          codigo={erro instanceof ErroDeApi ? erro.requestId : undefined}
          onTentarDeNovo={() => void resumoQuery.refetch()}
        />
      </>
    );
  }

  const resumo = resumoQuery.data;

  return (
    <>
      <CabecalhoDoFinanceiro
        titulo="Financeiro"
        subtitulo="Fluxo de caixa, entradas e saídas por centro de custo — e as horas de quadra que faltam cobrar."
        acoes={
          podeCriar && (
            <Botao icone="plus" onClick={abrirNovo}>
              Novo lançamento
            </Botao>
          )
        }
      />

      <Indicadores resumo={resumo} />
      <GraficosDoMes resumo={resumo} />
      {/* 06 + 11 — o que a agenda registrou e o caixa ainda não recebeu.
          Fica ACIMA do extrato de propósito: o extrato é o que já
          aconteceu, e isto é o que falta fazer acontecer. */}
      <CardDeHorasACobrar
        titulo="Horas de quadra a cobrar"
        de={`${resumo.mes}-01`}
        ate={`${resumo.mes}-${String(diasNoMes(resumo.mes)).padStart(2, '0')}`}
        dica={`${nomeDoMes(resumo.mes)} · para fechar o aluguel`}
        vazio="Nenhuma hora de quadra em aberto no mês."
        limite={8}
      />
      <CardDeExtrato query={extratoQuery} />

      {novo && podeCriar && <ModalDeLancamento hoje={resumo.hoje} onFechar={fecharNovo} />}
    </>
  );
}

function CabecalhoDoFinanceiro({
  titulo,
  subtitulo,
  acoes,
}: {
  titulo: string;
  subtitulo?: string;
  acoes?: React.ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <h1>{titulo}</h1>
        {subtitulo && <p>{subtitulo}</p>}
      </div>
      {acoes && <div className="head-actions">{acoes}</div>}
    </div>
  );
}

/**
 * Os quatro KPIs do topo. A tendência (alta/baixa/estável) segue o
 * SINAL aritmético puro da variação — nunca um julgamento de "menos
 * saída é bom" (documento 11: "não tente decidir se... siga o sinal").
 */
function Indicadores({ resumo }: { resumo: ResumoFinanceiro }) {
  const variacaoCaixa = resumo.caixa.variacaoSemana;
  const entradasPct = variacaoPercentual(resumo.mesAtual.entradas, resumo.mesAnterior.entradas);
  const saidasPct = variacaoPercentual(resumo.mesAtual.saidas, resumo.mesAnterior.saidas);
  const margem =
    resumo.mesAtual.entradas > 0
      ? (resumo.mesAtual.geracao / resumo.mesAtual.entradas) * 100
      : null;
  const mesAnteriorTexto = nomeDoMes(adicionarMeses(resumo.mes, -1));

  return (
    <div className="grid g4">
      <Kpi
        valor={dinheiro(resumo.caixa.saldo)}
        rotulo="Saldo em caixa hoje"
        tendencia={tendenciaDoSinal(variacaoCaixa)}
        variacao={
          variacaoCaixa === 0
            ? 'sem variação na semana'
            : `${variacaoCaixa > 0 ? '+' : '−'}${dinheiro(Math.abs(variacaoCaixa))} na semana`
        }
      />
      <Kpi
        valor={dinheiro(resumo.mesAtual.entradas)}
        rotulo="Entradas do mês"
        tendencia={tendenciaDoSinal(entradasPct ?? 0)}
        variacao={
          entradasPct === null
            ? 'sem mês anterior para comparar'
            : `${formatarPercentual(entradasPct)} vs. ${mesAnteriorTexto}`
        }
      />
      <Kpi
        valor={dinheiro(resumo.mesAtual.saidas)}
        rotulo="Saídas do mês"
        tendencia={tendenciaDoSinal(saidasPct ?? 0)}
        variacao={
          saidasPct === null
            ? 'sem mês anterior para comparar'
            : `${formatarPercentual(saidasPct)} vs. ${mesAnteriorTexto}`
        }
      />
      <Kpi
        valor={dinheiro(resumo.mesAtual.geracao)}
        rotulo="Geração de caixa"
        tendencia={tendenciaDoSinal(resumo.mesAtual.geracao)}
        variacao={
          // Na margem o sinal é a informação — "margem de 179%" num mês
          // que queimou caixa lê como lucro. Nas variações a seta já diz
          // a direção, e por isso `formatarPercentual` tira o sinal.
          margem === null
            ? 'sem entradas no mês'
            : `margem de ${margem < 0 ? '−' : ''}${formatarPercentual(margem)}`
        }
      />
    </div>
  );
}

/** "Entradas e saídas por semana" e "Saídas por categoria", lado a lado. */
function GraficosDoMes({ resumo }: { resumo: ResumoFinanceiro }) {
  const totalSaidas = resumo.mesAtual.saidas;

  return (
    <div className="grid g-2-1">
      <Card titulo="Entradas e saídas por semana" dica={nomeDoMes(resumo.mes)}>
        <Barras
          cores={['var(--serie-1)', 'var(--serie-3)']}
          legenda={[
            { rotulo: 'Entradas', cor: 'var(--serie-1)' },
            { rotulo: 'Saídas', cor: 'var(--serie-3)' },
          ]}
          colunas={resumo.semanas.map((semana) => ({
            x: semana.rotulo,
            valores: [semana.entradas, semana.saidas],
            dicas: [
              `Entradas de ${dinheiro(semana.entradas)}`,
              `Saídas de ${dinheiro(semana.saidas)}`,
            ],
          }))}
        />
      </Card>
      <Card titulo="Saídas por categoria" dica="mês corrente">
        {resumo.saidasPorCategoria.length === 0 ? (
          <Vazio>Nenhuma saída neste mês.</Vazio>
        ) : (
          <BarrasHorizontais
            itens={resumo.saidasPorCategoria.map((item, indice) => ({
              nome: item.categoria,
              valor: item.total,
              rotulo: dinheiro(item.total),
              cor: CORES_DAS_CATEGORIAS[indice % CORES_DAS_CATEGORIAS.length] as string,
              dica:
                totalSaidas > 0
                  ? `${formatarPercentual((item.total / totalSaidas) * 100)} das saídas.`
                  : dinheiro(item.total),
            }))}
          />
        )}
      </Card>
    </div>
  );
}

/**
 * "Últimos lançamentos" — só leitura e criação nesta fase: sem
 * clique-para-detalhe, sem estornar/realizar/cancelar (o protótipo
 * também não tem essa interação em `#v-fin`).
 */
function CardDeExtrato({ query }: { query: UseQueryResult<PaginaDoExtrato, unknown> }) {
  if (query.isPending) {
    return (
      <Card titulo="Últimos lançamentos">
        <EsqueletoDeTabela colunas={7} />
      </Card>
    );
  }

  if (query.isError) {
    const erro = query.error;
    return (
      <Card titulo="Últimos lançamentos">
        <Erro
          tipo={tipoDoErro(erro)}
          codigo={erro instanceof ErroDeApi ? erro.requestId : undefined}
          onTentarDeNovo={() => void query.refetch()}
        />
      </Card>
    );
  }

  const extrato = query.data;

  return (
    <Card titulo="Últimos lançamentos">
      {extrato.itens.length === 0 ? (
        <Vazio>Nenhum lançamento neste mês.</Vazio>
      ) : (
        <Tabela
          colunas={[
            'Data',
            'Descrição',
            'Categoria',
            'Centro de custo',
            'Forma',
            'Situação',
            'Valor',
          ]}
        >
          {extrato.itens.map((item) => (
            <tr key={item.id}>
              <td>{dataCurta(item.data)}</td>
              <td>{item.descricao}</td>
              <td>{item.categoria}</td>
              <td>
                <Tag>{ROTULO_CENTRO_DE_CUSTO[item.centroDeCusto]}</Tag>
              </td>
              <td>{item.formaPagamento ? ROTULO_FORMA_PAGAMENTO[item.formaPagamento] : '—'}</td>
              <td>
                <Status situacao={item.situacao === 'Realizado' ? 'ok' : 'aguardando'}>
                  {item.situacao}
                </Status>
              </td>
              <td
                className="num"
                style={{ color: item.tipo === 'Entrada' ? 'var(--ok-fg)' : 'var(--txt)' }}
              >
                {item.tipo === 'Entrada' ? '+' : '−'} {dinheiro(item.valor)}
              </td>
            </tr>
          ))}
        </Tabela>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------
// Formatação — puro, sem estado
// ---------------------------------------------------------------------

function tendenciaDoSinal(valor: number): Tendencia {
  if (valor > 0) return 'alta';
  if (valor < 0) return 'baixa';
  return 'estavel';
}

/** `null` quando o mês anterior é zero — não dá para dizer "cresceu X%" de uma base zero. */
function variacaoPercentual(atual: number, anterior: number): number | null {
  if (anterior === 0) return null;
  return ((atual - anterior) / anterior) * 100;
}

function formatarPercentual(valor: number): string {
  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(valor))}%`;
}

/** "2026-09" → "setembro". Meio-dia UTC: mesmo truque de `dataPorExtenso`, sem fuso empurrando o mês. */
function nomeDoMes(mes: string): string {
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', timeZone: 'UTC' }).format(
    new Date(`${mes}-01T12:00:00Z`),
  );
}

function tipoDoErro(erro: unknown): 'rede' | 'permissao' | 'falha' {
  if (erro instanceof ErroDeRede) return 'rede';
  if (erro instanceof ErroDeApi && erro.ehPermissao) return 'permissao';
  return 'falha';
}
